import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { NAV_ITEMS, ADMIN_NAV } from "@/lib/constants";
import api from "@/lib/api";
import { cn } from "@/lib/utils";
import { BrandLogo } from "@/components/Logo";
import {
  Menu, LogOut, Bell, LayoutDashboard, Search, PlusCircle, MessageSquare, User,
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

const initials = (name = "") => name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase() || "U";

function NavLinks({ onNavigate }) {
  const { user } = useAuth();
  const items = [...NAV_ITEMS];
  if (user?.role === "admin") items.push(ADMIN_NAV);
  return (
    <nav className="space-y-1">
      {items.map((it) => {
        const Icon = it.icon;
        return (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.to === "/dashboard"}
            data-testid={it.testid}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-colors",
                isActive ? "bg-primary text-primary-foreground shadow-sm" : "text-slate-600 hover:bg-accent hover:text-primary"
              )
            }
          >
            <Icon className="w-[18px] h-[18px]" />
            {it.label}
          </NavLink>
        );
      })}
    </nav>
  );
}

const MOBILE_TABS = [
  { to: "/dashboard", label: "Home", icon: LayoutDashboard, testid: "mtab-dashboard", end: true },
  { to: "/find-deliveries", label: "Search", icon: Search, testid: "mtab-search" },
  { to: "/create-delivery", label: "Send", icon: PlusCircle, testid: "mtab-create", primary: true },
  { to: "/messages", label: "Chat", icon: MessageSquare, testid: "mtab-messages" },
  { to: "/profile", label: "Profile", icon: User, testid: "mtab-profile" },
];

export function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const { data } = await api.get("/notifications/unread-count");
        if (active) setUnread(data.count);
      } catch {}
    };
    load();
    const t = setInterval(load, 15000);
    return () => { active = false; clearInterval(t); };
  }, []);

  const doLogout = async () => { await logout(); navigate("/"); };

  const Brand = (
    <Link to="/dashboard" className="flex items-center gap-2" data-testid="sidebar-logo">
      <BrandLogo />
    </Link>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col border-r border-slate-200 bg-white z-20">
        <div className="px-6 h-16 flex items-center border-b border-slate-100">{Brand}</div>
        <div className="flex-1 overflow-y-auto px-3 py-4 pm-scroll"><NavLinks /></div>
        <div className="p-3 border-t border-slate-100">
          <button onClick={doLogout} data-testid="sidebar-logout" className="flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium text-slate-600 hover:bg-rose-50 hover:text-rose-600 w-full transition-colors">
            <LogOut className="w-[18px] h-[18px]" /> Sign Out
          </button>
        </div>
      </aside>

      <div className="lg:pl-64">
        {/* Top header */}
        <header className="sticky top-0 z-20 h-16 flex items-center justify-between px-4 sm:px-6 bg-white/80 backdrop-blur-md border-b border-slate-200">
          <div className="flex items-center gap-3">
            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
              <SheetTrigger asChild>
                <button className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-accent" data-testid="mobile-menu-trigger">
                  <Menu className="w-5 h-5 text-slate-700" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0">
                <div className="px-6 h-16 flex items-center border-b border-slate-100">{Brand}</div>
                <div className="px-3 py-4 overflow-y-auto pm-scroll" style={{ maxHeight: "calc(100vh - 4rem)" }}>
                  <NavLinks onNavigate={() => setMenuOpen(false)} />
                </div>
              </SheetContent>
            </Sheet>
            <div className="lg:hidden">{Brand}</div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <button onClick={() => navigate("/notifications")} data-testid="header-notifications" className="relative p-2 rounded-lg hover:bg-accent">
              <Bell className="w-5 h-5 text-slate-700" />
              {unread > 0 && (
                <span data-testid="notification-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center">
                  {unread > 9 ? "9+" : unread}
                </span>
              )}
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 p-1 pr-2 rounded-full hover:bg-accent" data-testid="user-menu-trigger">
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-primary text-white text-xs">{initials(user?.name)}</AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:block text-sm font-medium text-slate-700 max-w-[120px] truncate">{user?.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="truncate">{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile">Profile</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">Settings</DropdownMenuItem>
                {user?.role === "admin" && <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">Admin</DropdownMenuItem>}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={doLogout} data-testid="menu-logout" className="text-rose-600">Sign Out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="px-4 sm:px-6 lg:px-8 py-6 pb-24 lg:pb-10 max-w-7xl mx-auto animate-fade-up">{children}</main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-20 bg-white/90 backdrop-blur-md border-t border-slate-200 flex items-center justify-around h-16 px-2">
        {MOBILE_TABS.map((t) => {
          const Icon = t.icon;
          return (
            <NavLink
              key={t.to}
              to={t.to}
              end={t.end}
              data-testid={t.testid}
              className={({ isActive }) =>
                cn("flex flex-col items-center justify-center gap-0.5 text-[11px] font-medium flex-1",
                  t.primary ? "" : isActive ? "text-primary" : "text-slate-500")
              }
            >
              {t.primary ? (
                <span className="w-11 h-11 -mt-4 rounded-2xl bg-primary text-white flex items-center justify-center shadow-lg shadow-primary/30">
                  <Icon className="w-5 h-5" />
                </span>
              ) : (
                <Icon className="w-5 h-5" />
              )}
              <span>{t.label}</span>
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}
